import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Network, Plus, Building2, Users, Briefcase, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function BusinessStructure() {
  const { id } = useParams(); // client_id
  const [structures, setStructures] = useState([]);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [entities, setEntities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEntityDialog, setShowEntityDialog] = useState(false);
  const [showRelDialog, setShowRelDialog] = useState(false);
  
  const [entityForm, setEntityForm] = useState({
    entity_type: 'individual',
    entity_name: '',
    entity_subtype: '',
    abn: '',
    is_root: false
  });
  
  const [relForm, setRelForm] = useState({
    from_entity_id: '',
    to_entity_id: '',
    relationship_type: 'shareholding',
    ownership_percentage: ''
  });

  useEffect(() => {
    loadStructures();
  }, [id]);

  const loadStructures = async () => {
    try {
      const response = await api.get(`/api/clients/${id}/business-structures`);
      setStructures(response.data || []);
      if (response.data && response.data.length > 0) {
        loadStructureDetail(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load structures');
    } finally {
      setLoading(false);
    }
  };

  const loadStructureDetail = async (structureId) => {
    try {
      const response = await api.get(`/api/business-structures/${structureId}`);
      setSelectedStructure(response.data.structure);
      setEntities(response.data.entities || []);
      setRelationships(response.data.relationships || []);
    } catch (error) {
      toast.error('Failed to load structure details');
    }
  };

  const handleCreateStructure = async () => {
    try {
      const response = await api.post('/api/business-structures', null, {
        params: {
          client_id: id,
          structure_name: 'Client Group Structure'
        }
      });
      toast.success('Structure created');
      loadStructures();
    } catch (error) {
      toast.error('Failed to create structure');
    }
  };

  const handleAddEntity = async (e) => {
    e.preventDefault();
    if (!selectedStructure) return;
    
    try {
      await api.post(`/api/business-structures/${selectedStructure.id}/entities`, {
        structure_id: selectedStructure.id,
        ...entityForm,
        ownership_percentage: entityForm.ownership_percentage ? parseFloat(entityForm.ownership_percentage) : undefined
      });
      
      toast.success('Entity added');
      setShowEntityDialog(false);
      setEntityForm({ entity_type: 'individual', entity_name: '', entity_subtype: '', abn: '', is_root: false });
      loadStructureDetail(selectedStructure.id);
    } catch (error) {
      toast.error('Failed to add entity');
    }
  };

  const handleAddRelationship = async (e) => {
    e.preventDefault();
    if (!selectedStructure) return;
    
    try {
      await api.post(`/api/business-structures/${selectedStructure.id}/relationships`, {
        structure_id: selectedStructure.id,
        ...relForm,
        ownership_percentage: relForm.ownership_percentage ? parseFloat(relForm.ownership_percentage) : undefined
      });
      
      toast.success('Relationship added');
      setShowRelDialog(false);
      setRelForm({ from_entity_id: '', to_entity_id: '', relationship_type: 'shareholding', ownership_percentage: '' });
      loadStructureDetail(selectedStructure.id);
    } catch (error) {
      toast.error('Failed to add relationship');
    }
  };

  const getEntityIcon = (type) => {
    if (type === 'individual') return <Users className="w-5 h-5" />;
    if (type === 'company') return <Building2 className="w-5 h-5" />;
    if (type === 'trust') return <Shield className="w-5 h-5" />;
    if (type === 'partnership') return <Briefcase className="w-5 h-5" />;
    return <Network className="w-5 h-5" />;
  };

  if (loading) {
    return <div className="p-6"><div className="animate-pulse h-96 bg-muted rounded"></div></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
            Business Structure
          </h1>
          <p className="text-muted-foreground mt-1">Map entity relationships for tax-aware AI advice</p>
        </div>
        {structures.length === 0 && (
          <Button onClick={handleCreateStructure} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" />
            Create Structure
          </Button>
        )}
      </div>

      {structures.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Network className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Structure Yet</h3>
            <p className="text-muted-foreground mb-4">Create a business structure to enable structure-aware tax research</p>
            <Button onClick={handleCreateStructure} className="bg-primary hover:bg-primary/90">
              Create Structure
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-3 gap-6">
          {/* Entities */}
          <Card className="col-span-1">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Entities ({entities.length})</CardTitle>
                <Dialog open={showEntityDialog} onOpenChange={setShowEntityDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Entity</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddEntity} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Entity Type</Label>
                        <Select value={entityForm.entity_type} onValueChange={(v) => setEntityForm({...entityForm, entity_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="individual">Individual</SelectItem>
                            <SelectItem value="company">Company</SelectItem>
                            <SelectItem value="trust">Trust</SelectItem>
                            <SelectItem value="partnership">Partnership</SelectItem>
                            <SelectItem value="smsf">SMSF</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Entity Name *</Label>
                        <Input value={entityForm.entity_name} onChange={(e) => setEntityForm({...entityForm, entity_name: e.target.value})} placeholder="Smith Family Trust" />
                      </div>
                      {entityForm.entity_type === 'trust' && (
                        <div className="space-y-2">
                          <Label>Trust Type</Label>
                          <Select value={entityForm.entity_subtype} onValueChange={(v) => setEntityForm({...entityForm, entity_subtype: v})}>
                            <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="discretionary">Discretionary Trust</SelectItem>
                              <SelectItem value="unit">Unit Trust</SelectItem>
                              <SelectItem value="family">Family Trust</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>ABN</Label>
                        <Input value={entityForm.abn} onChange={(e) => setEntityForm({...entityForm, abn: e.target.value})} />
                      </div>
                      <div className="flex gap-3">
                        <Button type="button" variant="outline" onClick={() => setShowEntityDialog(false)}>Cancel</Button>
                        <Button type="submit" className="bg-primary hover:bg-primary/90">Add Entity</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {entities.map(entity => (
                  <div key={entity.id} className="p-3 border rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-2">
                      {getEntityIcon(entity.entity_type)}
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{entity.entity_name}</p>
                        <Badge variant="outline" className="text-xs mt-1">{entity.entity_type}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Relationships */}
          <Card className="col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Relationships ({relationships.length})</CardTitle>
                <Dialog open={showRelDialog} onOpenChange={setShowRelDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" disabled={entities.length < 2}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Relationship
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Relationship</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddRelationship} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>From Entity</Label>
                        <Select value={relForm.from_entity_id} onValueChange={(v) => setRelForm({...relForm, from_entity_id: v})}>
                          <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                          <SelectContent>
                            {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Relationship Type</Label>
                        <Select value={relForm.relationship_type} onValueChange={(v) => setRelForm({...relForm, relationship_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="shareholding">Shareholding</SelectItem>
                            <SelectItem value="beneficiary">Beneficiary</SelectItem>
                            <SelectItem value="trustee">Trustee</SelectItem>
                            <SelectItem value="director">Director</SelectItem>
                            <SelectItem value="loan">Loan</SelectItem>
                            <SelectItem value="partner">Partner</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>To Entity</Label>
                        <Select value={relForm.to_entity_id} onValueChange={(v) => setRelForm({...relForm, to_entity_id: v})}>
                          <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                          <SelectContent>
                            {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {relForm.relationship_type === 'shareholding' && (
                        <div className="space-y-2">
                          <Label>Ownership %</Label>
                          <Input type="number" value={relForm.ownership_percentage} onChange={(e) => setRelForm({...relForm, ownership_percentage: e.target.value})} placeholder="100" />
                        </div>
                      )}
                      <div className="flex gap-3">
                        <Button type="button" variant="outline" onClick={() => setShowRelDialog(false)}>Cancel</Button>
                        <Button type="submit" className="bg-primary hover:bg-primary/90">Add Relationship</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {relationships.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No relationships defined</p>
                  <p className="text-xs mt-1">Add entities first, then define relationships</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {relationships.map(rel => (
                    <div key={rel.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-semibold">{rel.from_entity_name}</span>
                          <Badge variant="outline" className="text-xs">{rel.relationship_type}</Badge>
                          {rel.ownership_percentage && (
                            <Badge className="bg-accent text-accent-foreground text-xs">{rel.ownership_percentage}%</Badge>
                          )}
                          <span className="text-muted-foreground/70">→</span>
                          <span className="font-semibold">{rel.to_entity_name}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Integration Note */}
      {selectedStructure && entities.length > 0 && (
        <Card className="border-l-4 border-l-purple-500 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Network className="w-6 h-6 text-purple-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-purple-900 mb-2">AI Tax Research Integration</h4>
                <p className="text-sm text-purple-800 mb-3">
                  This business structure is now available to the Tax Research Assistant. When you ask tax questions, 
                  the AI will automatically consider this structure and provide entity-specific advice.
                </p>
                <p className="text-xs text-purple-700">
                  <strong>Example:</strong> Ask "Can we distribute income to Company B?" and the AI will check if Company B 
                  exists in this structure and provide tailored advice based on the relationships you've defined.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
